/**
 * The signed-in user's own page.
 *
 * It answers three questions a logged-in application is expected to answer without anyone
 * having to ask support: who am I signed in as, how do I sign in, and how do I get out —
 * here, and everywhere else.
 *
 * What it deliberately does not do is manage the GitHub connection. That lives in Settings
 * and is a different decision: this page is about the application account, the other is
 * about what that account is allowed to reach.
 */
import { LogOut, Settings2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from '@tanstack/react-router'

import { IdentityPanel } from '@/components/profile/IdentityPanel'
import { SessionsPanel } from '@/components/profile/SessionsPanel'
import { SignInMethodsPanel } from '@/components/profile/SignInMethodsPanel'
import { Button } from '@/components/ui/Controls'
import { PageHeader, Panel } from '@/components/ui/Panel'
import { QueryBoundary, Skeleton } from '@/components/ui/States'
import { useAuth } from '@/features/auth/useAuth'
import { useProfile } from '@/features/profile/useProfile'
import { formatTimestamp } from '@/lib/utils/format'

import type { components } from '@/types/generated/api'

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      className="grid grid-cols-1 gap-1 border-b border-line/60 px-4 py-2.5 last:border-b-0
        sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4"
    >
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="min-w-0 text-sm text-ink-dim">{value}</dd>
    </div>
  )
}

function GitHubPanel({ github }: { github: components['schemas']['GitHubSummary'] }) {
  return (
    <Panel
      title="Connected GitHub account"
      description="The account every tool call acts as"
      actions={
        <Link
          to="/settings"
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line-strong bg-surface
            px-2.5 text-xs font-medium text-ink shadow-card transition-colors hover:bg-hover"
        >
          <Settings2 aria-hidden="true" className="h-3.5 w-3.5" />
          Manage
        </Link>
      }
    >
      {github.connected ? (
        <dl>
          <Row
            label="Account"
            value={<span className="mono text-ok">{github.account_login ?? '—'}</span>}
          />
          <Row label="Name" value={github.account_name ?? '—'} />
          <Row
            label="Permissions"
            value={
              (github.scopes ?? []).length > 0 ? (
                <span className="mono text-xs">{(github.scopes ?? []).join(', ')}</span>
              ) : (
                '—'
              )
            }
          />
          <Row label="Connected" value={formatTimestamp(github.connected_at)} />
        </dl>
      ) : (
        <div className="px-4 py-6">
          <p className="text-sm text-ink-dim">No GitHub account is connected.</p>
          <p className="mt-1 text-xs text-ink-muted">
            Until one is, the assistant has no repositories to read and nothing to change.
          </p>
          <Link
            to="/settings"
            className="mt-3 inline-block text-xs text-primary-bright underline-offset-2 hover:underline"
          >
            Connect one in Settings →
          </Link>
        </div>
      )}
    </Panel>
  )
}

export function ProfilePage() {
  const profile = useProfile()
  const { signOut } = useAuth()
  const [signingOut, setSigningOut] = useState(false)

  return (
    <div>
      <PageHeader
        title="Profile"
        description="Your account, how you sign in, and everywhere you are currently signed in. Your GitHub credential is never shown here — or anywhere else in this interface."
        actions={
          <Button
            variant="danger"
            icon={LogOut}
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true)
              // The server revokes the session before the cookie is cleared, so a copy of
              // the token is useless the moment this returns.
              void signOut().finally(() => setSigningOut(false))
            }}
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </Button>
        }
      />

      <QueryBoundary
        isLoading={profile.isLoading}
        error={profile.error}
        onRetry={() => void profile.refetch()}
        loadingFallback={
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        }
      >
        {profile.data ? (
          <>
            <div className="grid gap-4 lg:grid-cols-2">
              <IdentityPanel profile={profile.data} />
              <SignInMethodsPanel profile={profile.data} />
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <GitHubPanel github={profile.data.github} />
              <Panel title="Account safety">
                <dl>
                  <Row
                    label="Session"
                    value="Held in an httpOnly cookie, revocable from this page"
                  />
                  <Row
                    label="Signed in"
                    value={`${profile.data.active_sessions} browser session${
                      profile.data.active_sessions === 1 ? '' : 's'
                    }${profile.data.mcp_tokens > 0 ? `, ${profile.data.mcp_tokens} MCP token${profile.data.mcp_tokens === 1 ? '' : 's'}` : ''}`}
                  />
                  <Row
                    label="Isolation"
                    value="Your repositories, history and executions are visible only to you"
                  />
                </dl>
              </Panel>
            </div>
            <div className="mt-4">
              <SessionsPanel />
            </div>
          </>
        ) : null}
      </QueryBoundary>
    </div>
  )
}
